// Get data
import { smartRound } from '@/shared/lib/math'
import { minutesToHours } from '@/shared/lib/time'
import dayjs from 'dayjs'
import { type Store, sample } from 'effector'

import { getStatisticsAppQuery } from '../api'
import { STATISTICS_RANGE_FORMATS } from '../lib/constants'
import type { StatisticsData } from '../lib/types/common'
import { StatisticsPageGate, rangeDebounced } from './statistics-range.model'

const safeDataMap = <T, R>(
  store: Store<T | null | undefined>,
  fn: (data: T) => R,
  defaultValue: R,
) => store.map((data) => (data ? fn(data) : defaultValue))

// Registrations
export const $registrations = safeDataMap(
  getStatisticsAppQuery.$data,
  (data) => ({
    ...data.registrations,
    dynamics: smartRound(data.registrations.dynamics, 2),
  }),
  { registrationsCount: 0, dynamics: 0 },
)

export const $registrationDynamicsCourse = $registrations.map(({ dynamics }) =>
  dynamics > 0 ? 'positive' : dynamics < 0 ? 'negative' : 'neutral',
)

// Statistics Data
const detectDateFormat = (dates: string[]): string => {
  const seenTimes = new Set<string>()
  const seenDays = new Set<string>()
  for (const dateStr of dates) {
    const date = dayjs(dateStr)
    const timeStr = date.format('HH:mm')
    const dayStr = date.format('DD')
    if (seenTimes.has(timeStr)) {
      if (seenDays.has(dayStr)) return STATISTICS_RANGE_FORMATS.year
      return STATISTICS_RANGE_FORMATS.month
    }
    seenTimes.add(timeStr)
    seenDays.add(dayStr)
  }
  return STATISTICS_RANGE_FORMATS.day
}

export const transformStatisticsData = (
  input: Record<string, number>,
  dataFn?: (data: number) => number,
): StatisticsData[] => {
  const dates = Object.keys(input)
  const dateFormat = detectDateFormat(dates)
  return dates.map((key) => {
    const date = dayjs(key)
    const data = dataFn ? dataFn(input[key]) : input[key]
    return { date: date.format(dateFormat), data }
  })
}

// Users Online
export const $usersOnline: Store<StatisticsData[]> =
  getStatisticsAppQuery.$data.map((data) => {
    if (!data) return []
    return transformStatisticsData(data.usersOnline)
  })

// Listens
export const $listensDuration: Store<StatisticsData[]> =
  getStatisticsAppQuery.$data.map((data) => {
    if (!data) return []
    return transformStatisticsData(data.listenDuration, (data) =>
      minutesToHours(data),
    )
  })

// Logins
export const $logins: Store<StatisticsData[]> = getStatisticsAppQuery.$data.map(
  (data) => {
    if (!data) return []
    return transformStatisticsData(data.logins)
  },
)

// Radio Launches
export const $radioLaunches: Store<StatisticsData[]> =
  getStatisticsAppQuery.$data.map((data) => {
    if (!data) return []
    return transformStatisticsData(data.radioLaunches)
  })

sample({
  clock: rangeDebounced,
  target: getStatisticsAppQuery.start,
})

sample({
  clock: StatisticsPageGate.close,
  target: getStatisticsAppQuery.reset,
})
